/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Focal point for images the site renders with `object-fit: cover`.
//
// Every such image ships in a frame whose shape differs from the file: the
// provider "sfeerbeeld" on the public profile page (a fixed 16:9 frame), the
// admin's page slots (heroes, section images, cards — see
// src/pages/admin/ui/imageFrames.ts for where each lands) and the Kennisbank
// article heroes. The uploaded file keeps its own aspect (the upload routes
// resize with fit:'inside' — they never crop), so the crop happens in the
// browser at render time, and without a stored focal point it is always dead
// centre. A portrait photo loses its top and bottom, and nobody sees that
// until the page is live — for a partner, that is after an admin approval
// round.
//
// One editor writes the value everywhere: ImageFramePositioner
// (src/components/ImageFramePositioner.tsx), the image dragged inside the real
// frame, via focusFromDrag below.
//
// Stored as a CSS `object-position` string ("<x>% <y>%") rather than an
// {x,y} object for two reasons: it is exactly what consumes the value, and the
// profile-suggestion machinery (server/data/profileSuggestions.ts) addresses
// every reviewable row as a single string — an object would have split one
// "bijsnijdpositie" row into two for the reviewing admin.
//
// '' means "not set" and renders as the CSS default (centre), so every existing
// profile keeps its current appearance byte-for-byte until someone deliberately
// moves the focus.
//
// This module must stay dependency-free and DOM-free: it is compiled into the
// server bundle via tsconfig.server.json (`include`). The format itself — the
// parser, the canonical writer and the cover-crop window — lives in plain JS
// in src/lib/imageFocusCore.js, because the bake (scripts/prerender.mjs) and
// the image worker need it too and cannot load TypeScript (since 2026-10-10:
// an article hero's share crop follows its position). This file is its typed
// facade; import from here everywhere TypeScript can.

import {
  CENTER_FOCUS as CORE_CENTER_FOCUS,
  clampFocusPct,
  formatFocus as coreFormatFocus,
  isValidFocus as coreIsValidFocus,
  normalizeFocus as coreNormalizeFocus,
  parseFocus as coreParseFocus,
} from './imageFocusCore.js';

export interface FocusPoint {
  /** 0–100, left → right. */
  x: number;
  /** 0–100, top → bottom. */
  y: number;
}

/** Aspect of the sfeerbeeld frame on the public profile page (ProviderProfilePage). */
export const PROFILE_PHOTO_ASPECT = 16 / 9;

export const CENTER_FOCUS: Readonly<FocusPoint> = CORE_CENTER_FOCUS;

/** True for '' and for the canonical stored form: two integer percentages. */
export function isValidFocus(value: unknown): boolean {
  return coreIsValidFocus(value);
}

const clampPct = (n: number): number => clampFocusPct(n);

/** Tolerant read for rendering. Anything unparseable falls back to centre. */
export function parseFocus(value: unknown): FocusPoint {
  return coreParseFocus(value);
}

/**
 * Canonical string for a point. Dead centre serialises to '' — "explicitly
 * centred" and "never set" render identically, so collapsing them keeps a
 * no-op adjustment out of the review queue.
 */
export function formatFocus(point: FocusPoint): string {
  return coreFormatFocus(point);
}

/**
 * Best-effort canonicalisation of an incoming value. A value that cannot be
 * parsed is handed back trimmed rather than coerced to '' — same rule as
 * normalizeWebsite in profileValidation.ts: coercing garbage to empty would
 * queue a silent *deletion* of a good focal point under a success message.
 * The caller refuses it through validateProfileRow → 'focus_invalid'.
 */
export function normalizeFocus(value: unknown): string {
  return coreNormalizeFocus(value);
}

/** Ready-to-use CSS value for an `object-fit: cover` image. */
export function focusToObjectPosition(value: unknown): string {
  const { x, y } = parseFocus(value);
  return `${x}% ${y}%`;
}

/**
 * True when the image already matches the frame closely enough that nothing
 * visible is cut — the positioner then says so instead of offering a handle
 * that does nothing. 1 % tolerance absorbs rounding in the stored pixel
 * dimensions.
 */
export function fitsWithoutCrop(naturalRatio: number, targetRatio: number): boolean {
  if (!Number.isFinite(naturalRatio) || naturalRatio <= 0) return true;
  return Math.abs(naturalRatio - targetRatio) / targetRatio < 0.01;
}

/**
 * Which axis the focal point can travel along. With `object-fit: cover` an
 * image wider than the frame is cropped left/right (full height kept), a
 * taller one top/bottom (full width kept), so at most one axis ever moves in a
 * given frame. 'none' when the image fits.
 */
export function focusAxis(naturalRatio: number, targetRatio: number): 'x' | 'y' | 'none' {
  if (fitsWithoutCrop(naturalRatio, targetRatio)) return 'none';
  return naturalRatio > targetRatio ? 'x' : 'y';
}

/**
 * Direct manipulation: the image is dragged INSIDE the frame, and moves 1:1
 * with the pointer. `dxFraction` / `dyFraction` are the pointer's travel since
 * pointer-down as a fraction of the FRAME's width / height; `startFocus` is
 * the focus at pointer-down (not the running value, so rounding never
 * accumulates over a long drag).
 *
 * With `object-fit: cover` an image wider than the frame (R > T) is scaled to
 * the frame's height and overflows by (R/T − 1) frame-widths; object-position
 * x% places x% of that overflow off the left edge. Dragging the image right by
 * dx frame-widths therefore lowers x by dx / (R/T − 1) × 100 — the image's
 * left side comes into view. Taller images (R < T) mirror that vertically with
 * (T/R − 1) frame-heights of overflow.
 *
 * The axis with no overflow in THIS frame keeps its start value, so one focus
 * point can be tuned on x in a phone frame and on y in a desktop band without
 * either frame undoing the other. Clamped to 0–100 and rounded, like
 * formatFocus.
 */
export function focusFromDrag(
  naturalRatio: number,
  targetRatio: number,
  startFocus: FocusPoint,
  dxFraction: number,
  dyFraction: number
): FocusPoint {
  const start = { x: clampPct(startFocus.x), y: clampPct(startFocus.y) };
  if (
    !Number.isFinite(naturalRatio) ||
    naturalRatio <= 0 ||
    !Number.isFinite(targetRatio) ||
    targetRatio <= 0
  ) {
    return start;
  }
  const axis = focusAxis(naturalRatio, targetRatio);
  if (axis === 'x') {
    const overflow = naturalRatio / targetRatio - 1;
    const dx = Number.isFinite(dxFraction) ? dxFraction : 0;
    return { x: clampPct(start.x - (dx / overflow) * 100), y: start.y };
  }
  if (axis === 'y') {
    const overflow = targetRatio / naturalRatio - 1;
    const dy = Number.isFinite(dyFraction) ? dyFraction : 0;
    return { x: start.x, y: clampPct(start.y - (dy / overflow) * 100) };
  }
  return start;
}

// A page slot's focus is stored as a SIBLING key in the same page object of
// image_mappings.json:
//
//   { "about": { "mission": "/uploads/x.webp", "mission@focus": "30% 60%" } }
//
// Values stay strings, so the file's shape `{ page: { slot: string } }`, the
// server's per-page merge on save, the content sync, the admin draft storage
// and the SSR injection all carry it unchanged — no second file, no schema
// bump. '@' never occurs in a slot key, so the suffix cannot collide with one.
const SLOT_FOCUS_SUFFIX = '@focus';

/** The image_mappings key that holds `slot`'s focus, e.g. "mission@focus". */
export function slotFocusKey(slot: string): string {
  return `${slot}${SLOT_FOCUS_SUFFIX}`;
}

/** True for a focus sibling key — i.e. a value that is NOT an image path. */
export function isSlotFocusKey(key: string): boolean {
  return typeof key === 'string' && key.endsWith(SLOT_FOCUS_SUFFIX);
}
